import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Register from './pages/Register';
import Verify from './pages/Verify';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MisConvocatorias from './pages/MisConvocatorias';
import ConvocatoriaForm from './pages/ConvocatoriaForm';
import VerConvocatoria from './pages/VerConvocatoria';
import EditarPerfil from './pages/EditarPerfil';
import UsuariosRegistrados from './pages/UsuariosRegistrados';
import ConvocatoriasDisponibles from './pages/ConvocatoriasDisponibles';
import PostularConvocatoria from './pages/PostularConvocatoria';
import MisPostulaciones from './pages/MisPostulaciones';
import GestionUsuarios from './pages/GestionUsuarios';
import ConsultaPostulaciones from './pages/ConsultaPostulaciones';
import DashboardEstadisticas from './pages/DashboardEstadisticas';
import GestionContratosResoluciones from './pages/GestionContratosResoluciones';
import LogsActividad from './pages/LogsActividad';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import EvaluadorDashboard from './pages/EvaluadorDashboard';
import MisNotificaciones from './pages/MisNotificaciones';
import SandboxLogin from './sandbox/SandboxLogin';
import SandboxFuncionarioDashboard from './sandbox/SandboxFuncionarioDashboard';
import SandboxValidadorDashboard from './sandbox/SandboxValidadorDashboard';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Forzar a usuarios legacy a completar/actualizar sus datos
  if (user.requires_data_update && location.pathname !== '/perfil/editar') {
    return <Navigate to="/perfil/editar" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route path="/verify" element={<Verify />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/convocatorias"
          element={
            <ProtectedRoute>
              <MisConvocatorias />
            </ProtectedRoute>
          }
        />
        <Route
          path="/convocatorias/nueva"
          element={
            <ProtectedRoute>
              <ConvocatoriaForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/convocatorias/:id"
          element={
            <ProtectedRoute>
              <VerConvocatoria />
            </ProtectedRoute>
          }
        />
        <Route
          path="/convocatorias/:id/editar"
          element={
            <ProtectedRoute>
              <ConvocatoriaForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/perfil/editar"
          element={
            <ProtectedRoute>
              <EditarPerfil />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios-registrados"
          element={
            <ProtectedRoute>
              <UsuariosRegistrados />
            </ProtectedRoute>
          }
        />
        <Route
          path="/convocatorias-disponibles"
          element={
            <ProtectedRoute>
              <ConvocatoriasDisponibles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/convocatoria/:id"
          element={
            <ProtectedRoute>
              <VerConvocatoria />
            </ProtectedRoute>
          }
        />
        <Route
          path="/postular/:id"
          element={
            <ProtectedRoute>
              <PostularConvocatoria />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mis-postulaciones"
          element={
            <ProtectedRoute>
              <MisPostulaciones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestion-usuarios"
          element={
            <ProtectedRoute>
              <GestionUsuarios />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consulta-postulaciones"
          element={
            <ProtectedRoute>
              <ConsultaPostulaciones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard-estadisticas"
          element={
            <ProtectedRoute>
              <DashboardEstadisticas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contratos-resoluciones"
          element={
            <ProtectedRoute>
              <GestionContratosResoluciones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs-actividad"
          element={
            <ProtectedRoute>
              <LogsActividad />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute>
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/evaluador"
          element={
            <ProtectedRoute>
              <EvaluadorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mis-notificaciones"
          element={
            <ProtectedRoute>
              <MisNotificaciones />
            </ProtectedRoute>
          }
        />
        {/* Sandbox / Capacitación (rutas públicas con auth propia) */}
        <Route path="/sandbox" element={<Navigate to="/sandbox/funcionario" replace />} />
        <Route path="/sandbox/funcionario" element={<SandboxLogin />} />
        <Route path="/sandbox/validador" element={<SandboxLogin />} />
        <Route path="/sandbox/funcionario/dashboard" element={<SandboxFuncionarioDashboard />} />
        <Route path="/sandbox/validador/dashboard" element={<SandboxValidadorDashboard />} />
      </Routes>
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;